import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';

import {
  NEO_FEEDBACK_ASK_HINT,
  NEO_FEEDBACK_ASK_PLACEHOLDER,
  NEO_FEEDBACK_ASK_TITLE,
  NEO_FEEDBACK_COMMENT_MAX,
  NEO_FEEDBACK_REASONS,
  NEO_FEEDBACK_SEND_FAILED,
  NEO_FEEDBACK_THANKS,
  neoFeedbackDetail,
  type NeoFeedbackAsk,
  type NeoFeedbackDetail,
  type NeoFeedbackRating,
  type NeoFeedbackReason,
} from '../lib/neoFeedback';
import { Icon } from './ui/Icon';

function ThumbsUpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}

function ThumbsDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  );
}

/**
 * The thumbs under an answered turn (ADR 0119) and, after a 👎, the ask for
 * what was wrong (TOM-461). The rating is recorded on the tap; the ask only
 * adds a reason and the owner's words to it.
 */
export function NeoFeedback({
  rating,
  ask,
  onRate,
  onSendDetail,
  onCloseAsk,
}: {
  rating: NeoFeedbackRating | undefined;
  ask: NeoFeedbackAsk | undefined;
  onRate: (rating: NeoFeedbackRating) => void;
  /** Posts the 👎 again with the reason and the words; true once stored. */
  onSendDetail: (detail: NeoFeedbackDetail) => Promise<boolean>;
  onCloseAsk: () => void;
}) {
  const downRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <div className="neo-feedback" role="group" aria-label="Rate this answer">
        <button
          type="button"
          className={`neo-feedback-button${rating === 'up' ? ' is-active' : ''}`}
          aria-pressed={rating === 'up'}
          aria-label="Helpful"
          title="Helpful"
          onClick={() => onRate('up')}
        >
          <ThumbsUpIcon />
        </button>
        <button
          ref={downRef}
          type="button"
          className={`neo-feedback-button${rating === 'down' ? ' is-active' : ''}`}
          aria-pressed={rating === 'down'}
          aria-label="Not helpful"
          title="Not helpful"
          onClick={() => onRate('down')}
        >
          <ThumbsDownIcon />
        </button>
      </div>
      {rating === 'down' && ask === 'open' ? (
        <NeoFeedbackAskForm onSend={onSendDetail} onClose={onCloseAsk} returnFocus={downRef} />
      ) : null}
      {rating === 'down' && ask === 'thanked' ? (
        <p className="neo-feedback-thanks" data-testid="neo-feedback-thanks">
          <Icon name="check" size={15} strokeWidth={2.4} />
          <span>{NEO_FEEDBACK_THANKS}</span>
        </p>
      ) : null}
    </>
  );
}

/**
 * The ask itself: the reasons as toggle chips (one at a time), the box, Send
 * and a close button. Escape closes it from anywhere inside, without closing
 * the panel it sits in; focus goes back to the 👎.
 */
function NeoFeedbackAskForm({
  onSend,
  onClose,
  returnFocus,
}: {
  onSend: (detail: NeoFeedbackDetail) => Promise<boolean>;
  onClose: () => void;
  returnFocus?: RefObject<HTMLButtonElement | null>;
}) {
  const [reason, setReason] = useState<NeoFeedbackReason | null>(null);
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'failed'>('idle');
  const rootRef = useRef<HTMLFormElement | null>(null);
  const titleId = useId();
  const detail = neoFeedbackDetail(reason, comment);

  // Opened under an answer near the bottom of the log, the ask may start below
  // what the log shows: bring it into view, inside the log only, so a page
  // under the panel never moves.
  useEffect(() => {
    const root = rootRef.current;
    const log = root?.closest('.neo-messages');
    if (root === null || log === null || log === undefined) return;
    const box = root.getBoundingClientRect();
    const view = log.getBoundingClientRect();
    const below = box.bottom - view.bottom + 16;
    const room = box.top - view.top;
    if (below > 0) log.scrollBy({ top: Math.min(below, room), behavior: 'smooth' });
  }, []);

  function close() {
    onClose();
    returnFocus?.current?.focus();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (detail === null || state === 'sending') return;
    setState('sending');
    const stored = await onSend(detail);
    if (!stored) {
      setState('failed');
      return;
    }
    // The ask gives way to the thank-you line; focus goes back to the 👎.
    returnFocus?.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return;
    // Only the ask closes: the panel's own Escape listens on the document.
    event.preventDefault();
    event.stopPropagation();
    close();
  }

  return (
    <form
      ref={rootRef}
      className="neo-feedback-ask"
      data-testid="neo-feedback-ask"
      aria-labelledby={titleId}
      onSubmit={(event) => void submit(event)}
      onKeyDown={onKeyDown}
    >
      <div className="neo-feedback-ask__head">
        <p className="neo-feedback-ask__title" id={titleId}>
          {NEO_FEEDBACK_ASK_TITLE}
        </p>
        <button
          type="button"
          className="neo-feedback-ask__close"
          aria-label="Close"
          title="Close"
          onClick={close}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="neo-feedback-ask__reasons" role="group" aria-labelledby={titleId}>
        {NEO_FEEDBACK_REASONS.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            className="neo-feedback-reason"
            aria-pressed={reason === code}
            onClick={() => {
              setReason((current) => (current === code ? null : code));
              if (state === 'failed') setState('idle');
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        className="neo-feedback-ask__comment"
        aria-label="What did you expect?"
        placeholder={NEO_FEEDBACK_ASK_PLACEHOLDER}
        rows={2}
        maxLength={NEO_FEEDBACK_COMMENT_MAX}
        value={comment}
        onChange={(event) => {
          setComment(event.target.value);
          if (state === 'failed') setState('idle');
        }}
      />
      {state === 'failed' ? (
        <p className="neo-feedback-ask__error" role="alert">
          {NEO_FEEDBACK_SEND_FAILED}
        </p>
      ) : null}
      <div className="neo-feedback-ask__foot">
        <p className="neo-feedback-ask__hint">{NEO_FEEDBACK_ASK_HINT}</p>
        <button
          type="submit"
          className="tb-btn tb-btn--tinted tb-btn--sm neo-feedback-ask__send"
          disabled={detail === null || state === 'sending'}
        >
          {state === 'sending' ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  );
}
